import './seamless-scroll-polyfill'
import './resizeObserver-polyfill'
import './HiyaJSBridge'
import 'core-js/stable'
import 'antd-mobile/es/global'
import {loadableReady} from '@loadable/component'
import * as Sentry from '@sentry/react'
import {BrowserTracing} from '@sentry/tracing'
import {createBrowserHistory} from 'history'
import App from 'hiya-common/src/App/index'
import {http} from 'hiya-common/src/utils/axios'
import React from 'react'
import {hydrate} from 'react-dom'
import {Provider} from 'react-redux'
import {renderRoutes} from 'react-router-config'
import {matchPath, Router} from 'react-router-dom'
import {StoreType} from '../stores/index'
import {isZY} from '../utils'
import {InitWebAnimationsJsPolyfill} from './web-animations-js'
import PendingNavDataLoader from './PendingNavDataLoader'
import {replaceSentryDsn} from '../utils/host/AutoHost'
import {reportRenderStart} from '../utils/pageLifecycle'
import {getCMid} from '../utils/cMid'
// import '../utils/inobounce'
// import 'inobounce'
import './index.scss'

const history = createBrowserHistory()
export {history}

const ignoredRoutesSentry = ['/ToyBricksEdit']

export const init = ({createStore, getRoutes, projectConfig}) => {
  const AppWithSentry = Sentry.withProfiler(App, {
    name: projectConfig.project,
  })

  const store: StoreType = createStore(window.APP_INITIAL_STATE)
  window.store = store
  getCMid()
  // window.APP_INITIAL_STATE = null
  http.setPublicFunction({
    log: console,
  })

  const routes = getRoutes(store)
  if (ENV !== 'development') {
    Sentry.init({
      // webpack打包时暴露的全局变量，在src/.xcsentryrc文件中定义
      dsn: replaceSentryDsn(XC_SENTRY_CLIENT),
      environment: ENV,
      integrations: [
        new BrowserTracing({
          // https://docs.sentry.io/platforms/javascript/guides/react/performance/instrumentation/automatic-instrumentation/
          tracingOrigins: projectConfig.tracingOrigins, // 不包含stat.izuiyou.com，避免给埋点请求添加sentry请求头导致埋点请求失败
          routingInstrumentation: Sentry.reactRouterV5Instrumentation(
            history,
            routes,
            matchPath
          ),
        }),
      ],
      ignoreErrors: [
        'Non-Error promise rejection captured with keys: message',
        "Can't find variable: HiyaJSBridge",
        'Non-Error promise rejection captured with keys: message, name, stack',
        'ResizeObserver loop limit exceeded',
        'Bad control character in string literal in JSON at position 248',
        'Bad control character in string literal in JSON at position',
        'HiyaJSBridge is not defined',
        "Invalid character '\u0301'",
        'Non-Error promise rejection captured with value:',
        'ResizeObserver loop completed with undelivered notifications.',
        'NotSupportedError: Failed to load because no supported source was found.',
        'Network Error', // 网络问题
        'ChunkLoadError', // 加载问题
        'An async animation has been interrupted', // await next问题 -实际动画需要无影响
        'Large Render Blocking Asset', // 大资源问题待查看
        'WebGL unsupported', // webGL不支持的报错
        `Cannot read properties of undefined (reading 'responseId')`, // jsbridge问题，待客户端解决放开
        `Cannot read property 'responseId' of undefined`, // jsbridge问题，待客户端解决放开
        `Error: SecurityError: Failed to get ServiceWorkerRegistration objects: No URL is associated with the caller's document.`, // jsbridge问题, 目前是webMessage方法报错： handler is not a function
        'Error: timeout of 10000ms exceeded', //  网络超时问题
        `Cannot read properties of null (reading 'batchSprite')`, // phaser 未知问题
        'Failed to start the audio device', // roulette 仅使用客户端播放
        `Error: Request aborted`, // 用户端请求中断问题
      ],
      tracesSampleRate: 0.07, // 测试环境调试可适当加大此值，线上建议根据项目日PV设置为0.05 ~ 0.1
      beforeSend(event) {
        const url = event.request?.url

        // 检查请求的路由
        if (
          ignoredRoutesSentry.some((route) => {
            return url?.includes(route)
          })
        ) {
          return null // 返回 null 将忽略这个事件
        }

        return event // 否则返回事件
      },
    })
    try {
      const data = store.getState().env
      Sentry.setUser({
        ...data,
        id: String(data.deviceinfo.h_m),
        ip_address: '{{auto}}',
      })
    } catch (error) {
      //
    }
  }

  const application = (
    <Provider store={store}>
      <Router history={history}>
        <PendingNavDataLoader store={store} routes={routes}>
          {() => <AppWithSentry>{renderRoutes(routes)}</AppWithSentry>}
        </PendingNavDataLoader>
      </Router>
    </Provider>
  )

  Promise.all([
    InitWebAnimationsJsPolyfill,
    new Promise((res) => {
      // ios 4.16.0 未给userAgent 写入 inapp
      if (isZY() || store.getState().env.deviceinfo?.h_av === '4.16.0') {
        if (window.HiyaJSBridge) {
          res(null)
        } else {
          document.addEventListener('HiyaJSBridgeReady', () => {
            res(null)
          })
        }
      } else {
        const nop = () => {
          // 防止端外报错
        }
        window.HiyaJSBridge = {
          registerHandler: nop,
          callHandler: nop,
          unregisterHandler: nop,
        }
        res(null)
      }
    }),
    new Promise((res) => {
      loadableReady(() => {
        res(null)
      })
    }),
  ]).then(() => {
    reportRenderStart(store.getState().env)
    const root = document.getElementById('app')
    hydrate(application, root)
  })
}
